import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, ShieldCheck, CheckCircle2, XCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MaskedLine } from "@/components/site/Reveal";
import { useLang } from "@/i18n";
import { CONSULT } from "@/i18n-consult";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Pagamento = () => {
  const [params] = useSearchParams();
  const { lang } = useLang();
  const c = CONSULT[lang].pay;
  const esito = params.get("esito");
  const sessionId = params.get("session_id");

  const [form, setForm] = useState({ name: "", email: "", amount: "", ref: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    if (esito === "ok" && sessionId) {
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts += 1;
        try {
          const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
          if (data.payment_status === "paid") {
            setPaid(true);
            clearInterval(poll);
          }
        } catch {}
        if (attempts > 10) clearInterval(poll);
      }, 2000);
      return () => clearInterval(poll);
    }
  }, [esito, sessionId]);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!form.name.trim()) er.name = "errRequired";
    if (!EMAIL_RE.test(form.email)) er.email = "errEmail";
    const amount = parseFloat(form.amount.replace(",", "."));
    if (!amount || amount <= 0 || amount > 50000) er.amount = "errAmount";
    if (!form.ref.trim()) er.ref = "errRequired";
    setErrors(er);
    if (Object.keys(er).length) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        name: form.name,
        email: form.email,
        amount,
        reference: form.ref,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      setErrors({ generic: "errGeneric" });
      setLoading(false);
    }
  };

  const fieldCls =
    "rounded-none border-ink/20 bg-white px-4 py-3 text-sm text-ink placeholder:text-mutedink/60 focus-visible:ring-1 focus-visible:ring-accent";

  if (esito === "ok") {
    return (
      <div className="bg-paper py-24 lg:py-32" data-testid="pagamento-page">
        <div className="mx-auto max-w-2xl px-6 text-center">
          {paid ? <CheckCircle2 className="mx-auto h-12 w-12 text-accent" strokeWidth={1.5} /> : <Loader2 className="mx-auto h-10 w-10 animate-spin text-accent" />}
          <h1 className="mt-6 font-serif text-4xl font-light text-ink" data-testid="pay-success-panel">{c.successTitle}</h1>
          <p className="mt-4 text-base leading-relaxed text-mutedink">{c.successBody}</p>
          <Link to="/" data-testid="pay-back-home" className="mt-8 inline-flex items-center gap-3 bg-ink px-8 py-4 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors hover:bg-accent">
            {c.back} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  if (esito === "annullato") {
    return (
      <div className="bg-paper py-24 lg:py-32" data-testid="pagamento-page">
        <div className="mx-auto max-w-2xl px-6 text-center">
          <XCircle className="mx-auto h-12 w-12 text-mutedink" strokeWidth={1.5} />
          <h1 className="mt-6 font-serif text-4xl font-light text-ink" data-testid="pay-cancel-panel">{c.cancelTitle}</h1>
          <p className="mt-4 text-base leading-relaxed text-mutedink">{c.cancelBody}</p>
          <Link to="/pagamento" data-testid="pay-retry" className="mt-8 inline-flex items-center gap-3 bg-ink px-8 py-4 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors hover:bg-accent">
            {c.submit} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-paper py-20 lg:py-28" data-testid="pagamento-page">
      <div className="mx-auto max-w-3xl px-6 sm:px-8">
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-accent">
          <span className="h-px w-10 bg-accent" /> {c.label}
        </motion.p>
        <h1 className="font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl" data-testid="pay-headline">
          <MaskedLine delay={0.1}>{c.l1}</MaskedLine>
          <MaskedLine delay={0.25}><span className="italic text-accent">{c.l2}</span></MaskedLine>
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }} className="mt-6 max-w-xl text-base leading-relaxed text-mutedink">
          {c.sub}
        </motion.p>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          onSubmit={submit}
          noValidate
          className="mt-12 border border-ink/12 bg-white p-8 lg:p-10"
          data-testid="payment-form"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="pay-name" className="text-xs uppercase tracking-[0.12em] text-mutedink">{c.name}</Label>
              <Input id="pay-name" value={form.name} onChange={set("name")} placeholder={c.phName} className={fieldCls} data-testid="pay-input-name" />
              {errors.name && <p className="text-xs text-red-700" data-testid="pay-error-name">{c[errors.name]}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="pay-email" className="text-xs uppercase tracking-[0.12em] text-mutedink">{c.email}</Label>
              <Input id="pay-email" type="email" value={form.email} onChange={set("email")} placeholder={c.phEmail} className={fieldCls} data-testid="pay-input-email" />
              {errors.email && <p className="text-xs text-red-700" data-testid="pay-error-email">{c[errors.email]}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="pay-amount" className="text-xs uppercase tracking-[0.12em] text-mutedink">{c.amount}</Label>
              <Input id="pay-amount" inputMode="decimal" value={form.amount} onChange={set("amount")} placeholder={c.phAmount} className={fieldCls} data-testid="pay-input-amount" />
              {errors.amount && <p className="text-xs text-red-700" data-testid="pay-error-amount">{c[errors.amount]}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="pay-ref" className="text-xs uppercase tracking-[0.12em] text-mutedink">{c.ref}</Label>
              <Input id="pay-ref" value={form.ref} onChange={set("ref")} placeholder={c.phRef} className={fieldCls} data-testid="pay-input-ref" />
              {errors.ref && <p className="text-xs text-red-700" data-testid="pay-error-ref">{c[errors.ref]}</p>}
            </div>
          </div>
          {errors.generic && <p className="mt-4 text-sm text-red-700" data-testid="pay-error-generic">{c[errors.generic]}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="pay-submit-button"
            className="mt-8 inline-flex w-full items-center justify-center gap-3 bg-ink px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-accent disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            {loading ? c.sending : c.submit}
          </button>
          <p className="mt-4 flex items-center justify-center gap-2 text-xs text-mutedink">
            <ShieldCheck className="h-4 w-4 text-accent" strokeWidth={1.5} /> {c.secure}
          </p>
        </motion.form>
      </div>
    </div>
  );
};

export default Pagamento;
